/* eslint-disable @typescript-eslint/indent */
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';

import { customersApi } from 'api/bestpark/customers';
import { geoApi } from 'api/bestpark/geolocation';
import { useAuth } from 'contexts/auth-context';
import { getItem } from 'helpers/local-storage';
import { parseQuery } from 'helpers/path-helpers';
import { getCurrentSession, AuthSession } from 'helpers/user-helpers';
import useNav from 'hooks/nav';
import { useTranslate } from 'hooks/translate';
import {
  getCountries,
  parseJsonData,
  isValidMembershipNumber,
} from 'helpers/utils';

import { LOGIN_PATH, PROFILE_PATH, TERMS } from 'constants/paths';
import { LOCAL_STORAGE_RESERVATION } from 'constants/local-storage';
import Alert from 'components/alerts/alert';
import './user-information.scss';
import {
  getH1Style,
  getSelectStyle,
  getFormStyle,
  getInputStyle,
} from './style-utils';

interface RegistrationFormValues {
  firstName: string;
  lastName: string;
  email: string;
  street1: string;
  postalCode: string;
  town: string;
  country: string;
  licensePlate: string;
  membershipNumberSfc: string;
  membershipNumberCci: string;
  isMarketing: boolean;
}

interface UserInformationProps {
  next?: Function;
  user?: Customer;
  hostedCamp?: boolean;
}

const INPUT_ERROR = 'input-error';

export default function UserInformation({
  next,
  user,
  hostedCamp,
}: UserInformationProps): ReactElement {
  const location = useLocation();
  const { goTo, locale } = useNav();
  const { data } = useQuery(['userLang'], geoApi.getGeolocationData);
  const { t } = useTranslate();
  const { authState, setAuthState, setUser } = useAuth();
  const [phoneNumber] = useState(getItem('BC.REGISTRATION_PHONE_NUMBER') ?? '');
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [membership] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState(
    `${
      user?.country
        ? user?.country?.toLowerCase()
        : data?.country?.toLowerCase() ?? ''
    }`,
  );
  const countries = getCountries(locale);
  const membershipError = t('reservation.registration.errors.membership');
  const userSchema = yup.object().shape<RegistrationFormValues>({
    firstName: yup
      .string()
      .required()
      .max(50),
    lastName: yup
      .string()
      .required()
      .max(50),
    email: yup
      .string()
      .required()
      .email(t('reservation.registration.errors.email')),
    street1: yup.string().max(50),
    postalCode: yup.string().max(50),
    town: yup.string().max(50),
    country: yup
      .string()
      .required()
      .max(60),
    licensePlate: yup
      .string()
      .required()
      .max(50),
    membershipNumberSfc: yup
      .string()
      .test('membership-number-test', membershipError, isValidMembershipNumber),
    membershipNumberCci: yup
      .string()
      .test('membership-number-test', membershipError, isValidMembershipNumber),
    isMarketing: yup.bool(),
  });
  const { errors, handleSubmit, register } = useForm<RegistrationFormValues>({
    validationSchema: userSchema,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      street1: user?.street,
      postalCode: user?.postalCode,
      town: user?.town,
      country: user?.country
        ? user?.country?.toLowerCase()
        : data?.country?.toLowerCase() ?? '',
      licensePlate: user?.licensePlate,
      membershipNumberSfc: user?.membershipNumberSfc ?? '',
      membershipNumberCci: user?.membershipNumberCci ?? '',
      isMarketing: user?.isMarketing,
    },
  });
  const onCountrySelect = (event: any): void =>
    setSelectedCountry(event.target.value);

  const onSubmit = useCallback(
    async ({
      town,
      street1,
      ...data
    }: RegistrationFormValues): Promise<void> => {
      const street = `${street1}`;
      setLoading(true);

      const reserve = getItem(LOCAL_STORAGE_RESERVATION);
      const park = parseJsonData(reserve);

      try {
        if (user) {
          const updatedUser = {
            ...user,
            ...data,
            town,
            street,
            phoneNumber,
            membershipId: membership,
          };
          const result = await customersApi.updateCustomer(updatedUser);

          if (result) {
            if (park.parkId !== 0 && next !== undefined) {
              next();
            } else {
              const session: AuthSession = await getCurrentSession();
              const interval = setInterval(() => {
                setAuthState(session);
                goTo(PROFILE_PATH);
                clearInterval(interval);
              }, 500);
            }
          }
        } else {
          const queryStrings = parseQuery<any>(location.search);
          if (hostedCamp) {
            // Set these values explicitly for hosted camp since form fields are not shown
            data.isMarketing = false;
            data.membershipNumberSfc = '';
            data.membershipNumberCci = '';
          }
          const result = await customersApi.postCustomer({
            ...data,
            street,
            phoneNumber,
            marketingCode: decodeURIComponent(queryStrings.code ?? ''),
            lang: locale,
          });

          if (result) {
            const session = await getCurrentSession();
            await setAuthState(session);
            const userData: Customer = {
              ...data,
              town: town,
              id: result,
              clv: '',
              street,
              phoneNumber,
            };
            setUser(userData);

            if (park.parkId !== 0 && next !== undefined) {
              next();
            } else {
              goTo(PROFILE_PATH);
            }
          }
        }
      } catch (error) {
        console.log(error);
      }

      setLoading(false);
    },
    [
      goTo,
      locale,
      location.search,
      next,
      phoneNumber,
      setAuthState,
      setUser,
      user,
      membership,
      hostedCamp,
    ],
  );
  const onAccept = (event: any): void => setAgreed(event.target.checked);

  useEffect(() => {
    if (!authState?.userSession) {
      goTo(`${LOGIN_PATH}${location.search}`);
    }
  }, [authState, goTo, location.search]);

  const requiredFieldMessage = t(
    'reservation.registration.errors.fieldRequired',
  );
  const tooLongValue = 'value is too long';

  const styleOverrideInput = getInputStyle(hostedCamp);
  const styleOverrideH1 = getH1Style(hostedCamp);
  const styleOverrideSelect = getSelectStyle(hostedCamp);
  const styleOverrideForm = getFormStyle(hostedCamp);

  const validationError =
    Object.values(errors).length > 0 ? Object.values(errors)[0] : undefined;
  return (
    <div className='booking-content booking-user-information'>
      <h1 style={styleOverrideH1}>{t('reservation.registration.header')}</h1>
      <div className='booking-forms' style={styleOverrideForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='row'>
            <div className='col'>
              <label>{`${t('reservation.registration.firstName')} *`}</label>
              <input
                className={errors?.firstName?.message && INPUT_ERROR}
                style={styleOverrideInput}
                disabled={isLoading}
                id='firstName'
                name='firstName'
                ref={register()}
                type='text'
              />
            </div>
            <div className='col'>
              <label>{`${t('reservation.registration.lastName')} *`}</label>
              <input
                className={errors?.lastName?.message && INPUT_ERROR}
                style={styleOverrideInput}
                disabled={isLoading}
                id='lastName'
                name='lastName'
                ref={register()}
                type='text'
              />
            </div>
          </div>
          {(errors.firstName ?? errors.lastName) && (
            <Alert message={requiredFieldMessage} />
          )}
          <div className='row'>
            <div className='col'>
              <label>{`${t('reservation.registration.email')} *`}</label>
              <input
                className={errors?.email?.message && INPUT_ERROR}
                style={styleOverrideInput}
                disabled={isLoading}
                id='email'
                name='email'
                ref={register()}
                type='email'
              />
            </div>
          </div>
          {errors.email && errors.email.type === 'required' && (
            <Alert message={requiredFieldMessage} />
          )}
          {errors.email && errors.email.type === 'email' && (
            <Alert message={t('reservation.registration.errors.email')} />
          )}
          <div className='row'>
            <div className='col'>
              <label>{t('reservation.registration.street')}</label>
              <input
                className={errors?.street1?.message && INPUT_ERROR}
                style={styleOverrideInput}
                disabled={isLoading}
                id='street1'
                name='street1'
                ref={register()}
                type='text'
              />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <label>{t('reservation.registration.postalCode')}</label>
              <input
                className={errors?.postalCode?.message && INPUT_ERROR}
                style={styleOverrideInput}
                disabled={isLoading}
                id='postalCode'
                name='postalCode'
                ref={register()}
                type='text'
              />
            </div>
            <div className='col'>
              <label>{t('reservation.registration.town')}</label>
              <input
                className={errors?.town?.message && INPUT_ERROR}
                style={styleOverrideInput}
                disabled={isLoading}
                id='town'
                name='town'
                ref={register()}
                type='text'
              />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <label>{`${t('reservation.registration.country')} *`}</label>
              <div className='select-container' style={styleOverrideInput}>
                <select
                  disabled={isLoading}
                  style={styleOverrideSelect}
                  id='country'
                  name='country'
                  onChange={onCountrySelect}
                  ref={register()}
                  value={selectedCountry}
                >
                  {countries.map((country, index) => (
                    <option
                      key={index}
                      value={country.value}
                      disabled={!country.value}
                    >
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <label>{`${t('reservation.registration.license')} *`}</label>
              <input
                className={errors?.licensePlate?.message && INPUT_ERROR}
                style={styleOverrideInput}
                disabled={isLoading}
                id='licensePlate'
                name='licensePlate'
                ref={register()}
                type='text'
              />
            </div>
          </div>
          {errors.licensePlate && <Alert message={requiredFieldMessage} />}
          {/** Do not show membership card fields if camp is a hosted camp (means it has a host url).
           */}
          {!hostedCamp && (
            <div className='row'>
              <div className='col' style={{ marginRight: 0 }}>
                <label>{t('reservation.registration.membershipCard1')}</label>
                <div className='membership-card-container'>
                  <div className='membership-card'>
                    <input
                      className={`${errors?.membershipNumberSfc?.message &&
                        INPUT_ERROR}`}
                      style={styleOverrideInput}
                      disabled={isLoading}
                      id='membershipNumberSfc'
                      name='membershipNumberSfc'
                      placeholder='000000-0'
                      ref={register()}
                      type='text'
                    />
                  </div>
                </div>
              </div>
              {/** C.C.I. membership number is hidden from the customers but an own field is implemented for it
               * in case it is needed in the future. Just remove membership-card-box class and it will become visible. Remove
               * also style={{ marginRight: 0 }} from the upper div with class col.
               */}
              <div className='col membership-card-box'>
                <label>{t('reservation.registration.membershipCard2')}</label>
                <div className='membership-card-container'>
                  <div>
                    <input
                      className={`${errors?.membershipNumberCci?.message &&
                        INPUT_ERROR}`}
                      style={styleOverrideInput}
                      disabled={isLoading}
                      id='membershipNumberCci'
                      name='membershipNumberCci'
                      placeholder='000000-0'
                      ref={register()}
                      type='text'
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {errors.membershipNumberSfc && (
            <Alert
              message={
                errors.membershipNumberSfc?.message
                  ? errors.membershipNumberSfc?.message.toString()
                  : membershipError
              }
            />
          )}
          {errors.membershipNumberCci && (
            <Alert
              message={
                errors.membershipNumberCci?.message
                  ? errors.membershipNumberCci?.message.toString()
                  : membershipError
              }
            />
          )}
          <div className='row'>
            <div className='col checkbox'>
              <label className='no-margin small-font'>
                {t('reservation.payment.terms1')}{' '}
                <a
                  href={`${process.env.REACT_APP_BESTCAMP_WP ??
                    ''}/${locale}${TERMS}`}
                  // eslint-disable-next-line react/jsx-no-target-blank
                  target='_blank'
                >
                  {t('reservation.payment.terms2')}
                </a>{' '}
                *
                <input checked={agreed} type='checkbox' onChange={onAccept} />
                <span className='checkmark' />
              </label>
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <div className='sub-text padded'>
                <small>{t('reservation.payment.terms3')}</small>
              </div>
            </div>
          </div>
          {!hostedCamp && (
            <div className='row'>
              <div className='col checkbox'>
                <label className='no-margin'>
                  {t('reservation.registration.marketing')}
                  <input name='isMarketing' ref={register()} type='checkbox' />
                  <span className='checkmark' />
                </label>
              </div>
            </div>
          )}
          {!hostedCamp && (
            <div className='row'>
              <div className='col'>
                <div className='sub-text padded'>
                  <small>{t('reservation.registration.newsletter')}</small>
                </div>
              </div>
            </div>
          )}
          {validationError && validationError.type === 'max' && (
            <Alert message={tooLongValue} />
          )}
          <button disabled={!agreed || isLoading} type='submit'>
            {isLoading ? t('loading') : t('reservation.registration.next')}
          </button>
        </form>
      </div>
    </div>
  );
}
